/**
 * Category taxonomy: Hebrew labels + icon keys for every Financy `main/sub` observed live, the binding
 * mapping to the 10 personal display groups, and `spendingScope()` (ARCHITECTURE.md §4).
 *
 * Financy does not expose its taxonomy endpoint on this plan, so this file IS the label source.
 * Unknown keys fall back to the `OTHER` group / "אחר" label — but the snapshot test fails on any
 * observed pair that is missing here, so additions are deliberate.
 */
import { isCardChargeRow, isReturnedPayment } from './normalize.ts';
import type { NormalizedTransaction } from './normalize.ts';

/** The 10 personal display groups (spec-personal §7.3, order = display order). */
export const GROUP_KEYS = [
  'HOUSING',
  'GROCERIES',
  'KIDS',
  'TRANSPORT',
  'DINING',
  'SHOPPING',
  'INSURANCE',
  'HEALTH',
  'TELECOM',
  'OTHER',
] as const;

export type GroupKey = (typeof GROUP_KEYS)[number];

/** Label + icon key per group (icon keys match `mockups/personal.html` `CATS`). */
export const GROUPS: Readonly<Record<GroupKey, { label: string; icon: string }>> = {
  HOUSING: { label: 'דיור וחשבונות', icon: 'home' },
  GROCERIES: { label: 'מזון וסופר', icon: 'basket' },
  KIDS: { label: 'ילדים וחינוך', icon: 'graduation' },
  TRANSPORT: { label: 'תחבורה ורכב', icon: 'car' },
  DINING: { label: 'מסעדות ובילוי', icon: 'utensils' },
  SHOPPING: { label: 'קניות וביגוד', icon: 'bag' },
  INSURANCE: { label: 'ביטוח', icon: 'shield' },
  HEALTH: { label: 'בריאות', icon: 'heart' },
  TELECOM: { label: 'תקשורת ומנויים', icon: 'phone' },
  OTHER: { label: 'אחר', icon: 'dashed' },
};

/** Hebrew label + icon for every observed `category.main`. */
export const MAIN_LABELS: Readonly<Record<string, { label: string; icon: string }>> = {
  'INCOMES_EXPENSES': { label: 'הכנסות והוצאות', icon: 'arrows' },
  'TRANSFER': { label: 'העברות', icon: 'transfer' },
  'FOOD_&_DRINKS': { label: 'אוכל ושתייה', icon: 'utensils' },
  'SHOPPING': { label: 'קניות', icon: 'bag' },
  'FINANCE': { label: 'פיננסים', icon: 'landmark' },
  'OTHER': { label: 'אחר', icon: 'dashed' },
  'HOUSEHOLD_&_SERVICES': { label: 'משק בית ושירותים', icon: 'home' },
  'DEPOSIT': { label: 'הפקדות', icon: 'deposit' },
  'LOAN_TRANSACTION': { label: 'הלוואות', icon: 'loan' },
  'TRANSPORT': { label: 'תחבורה', icon: 'car' },
  'RETURN': { label: 'החזרים', icon: 'undo' },
  'INSURANCE': { label: 'ביטוח', icon: 'shield' },
  'HOK_RETURN': { label: 'הוראת קבע שחזרה', icon: 'alert' },
  'CHQ_RETURN': { label: 'שיק שחזר', icon: 'alert' },
  'UNCATEGORIZED': { label: 'לא מסווג', icon: 'dashed' },
  'ASSETS': { label: 'נכסים וחיסכון', icon: 'piggy' },
  'NATIONAL_INSURANCE': { label: 'ביטוח לאומי', icon: 'landmark' },
  'LEISURE': { label: 'פנאי ובילוי', icon: 'ticket' },
  'HEALTH_&_BEAUTY': { label: 'בריאות ויופי', icon: 'heart' },
  'VAT': { label: 'מע"מ', icon: 'landmark' },
  'NEGATIVE_BEHAVIOR': { label: 'התנהלות חריגה', icon: 'alert' },
  'HOME_IMPROVEMENTS': { label: 'שיפוץ ובית', icon: 'hammer' },
  'PENSION': { label: 'פנסיה', icon: 'piggy' },
  'TRADING': { label: 'מסחר ומט"ח', icon: 'chart' },
};

/** Hebrew label for every observed `main/sub` pair. Key = `MAIN/SUB`. */
export const SUB_LABELS: Readonly<Record<string, string>> = {
  'INCOMES_EXPENSES/OTHER': 'הכנסות והוצאות — אחר',
  'INCOMES_EXPENSES/CREDIT_CARD_CHECKING': 'חיוב כרטיס אשראי',
  'TRANSFER/BANK_TRANSFER': 'העברה בנקאית',
  'TRANSFER/BIT_PAYBOX': 'ביט / פייבוקס',
  'FOOD_&_DRINKS/RESTAURANT': 'מסעדות',
  'FOOD_&_DRINKS/GROCERIES': 'סופרמרקט',
  'FOOD_&_DRINKS/COFFEE_&_SNACKS': 'קפה וחטיפים',
  'FOOD_&_DRINKS/ALCOHOL_&_TOBACCO': 'אלכוהול וטבק',
  'FOOD_&_DRINKS/BARS': 'ברים',
  'SHOPPING/SHOPPING_OTHER': 'קניות — אחר',
  'SHOPPING/ELECTRONICS': 'אלקטרוניקה',
  'SHOPPING/CLOTHES_&_ACCESSORIES': 'ביגוד ואקססוריז',
  'SHOPPING/BOOKS_&_GAMES': 'ספרים ומשחקים',
  'SHOPPING/HOBBY_&_SPORTS_EQUIPMENT': 'ציוד ספורט ותחביבים',
  'FINANCE/FINANCE_OTHER': 'פיננסים — אחר',
  'FINANCE/FEES': 'עמלות',
  'OTHER/BUSINESS_EXPENSES': 'הוצאות עסקיות',
  'OTHER/OTHER': 'אחר',
  'OTHER/PETS': 'חיות מחמד',
  'OTHER/EDUCATION': 'חינוך',
  'HOUSEHOLD_&_SERVICES/COMMUNICATIONS': 'תקשורת',
  'HOUSEHOLD_&_SERVICES/INSURANCE_&_FEES': 'ביטוח ועמלות',
  'HOUSEHOLD_&_SERVICES/UTILITIES': 'חשבונות בית',
  'DEPOSIT/CASH_INCOME': 'הפקדת מזומן',
  'DEPOSIT/CHQ_INCOME': 'הפקדת שיק',
  'LOAN_TRANSACTION/LOAN': 'החזר הלוואה',
  'LOAN_TRANSACTION/MORTGAGE': 'משכנתא',
  'TRANSPORT/TAXI': 'מוניות',
  'TRANSPORT/CAR_&_FUEL': 'רכב ודלק',
  'TRANSPORT/TRANSPORT_OTHER': 'תחבורה — אחר',
  'RETURN/NON_REVENUE': 'החזר שאינו הכנסה',
  'RETURN/REFUND': 'זיכוי',
  'INSURANCE/INSURANCE': 'ביטוח',
  'HOK_RETURN/HOK_AKAM_COMMISSION': 'עמלת הוראת קבע שחזרה',
  'HOK_RETURN/FUNDS_NOT_COVER': 'הוראת קבע חזרה — אין כיסוי',
  'HOK_RETURN/COMMISSION': 'עמלה',
  'HOK_RETURN/NOT_CLASSIFIED': 'הוראת קבע חזרה — לא מסווג',
  'CHQ_RETURN/FUNDS_NOT_COVER': 'שיק חזר — אין כיסוי',
  'CHQ_RETURN/CHQ_AKAM_COMMISSION': 'עמלת שיק שחזר',
  'CHQ_RETURN/CHQ_COMMISSION': 'עמלת שיק',
  'CHQ_RETURN/NOT_CLASSIFIED': 'שיק חזר — לא מסווג',
  'CHQ_RETURN/TECHNI': 'שיק חזר — טכני',
  'UNCATEGORIZED/UNCATEGORIZED': 'לא מסווג',
  'ASSETS/SAVINGS': 'הפקדה לחיסכון',
  'ASSETS/INTEREST_RATE': 'ריבית',
  'NATIONAL_INSURANCE/NOT_RESERVE_DUTY': 'ביטוח לאומי',
  'LEISURE/LEISURE_OTHER': 'פנאי — אחר',
  'LEISURE/VACATION': 'חופשה',
  'LEISURE/CULTURE_&_EVENTS': 'תרבות ואירועים',
  'LEISURE/HOBBIES': 'תחביבים',
  'HEALTH_&_BEAUTY/HEALTHCARE': 'רפואה',
  'HEALTH_&_BEAUTY/PHARMACY': 'בית מרקחת',
  'HEALTH_&_BEAUTY/EYECARE': 'אופטיקה',
  'HEALTH_&_BEAUTY/BEAUTY': 'טיפוח',
  'VAT/VAT': 'מע"מ',
  'NEGATIVE_BEHAVIOR/ALERT_NOTICE': 'הודעת אזהרה מהבנק',
  'HOME_IMPROVEMENTS/RENOVATION_&_REPAIRS': 'שיפוצים ותיקונים',
  'HOME_IMPROVEMENTS/FURNITURE_&_INTERIOR': 'ריהוט ועיצוב הבית',
  'HOME_IMPROVEMENTS/GARDEN': 'גינה',
  'PENSION/RECENT_PENSION': 'פנסיה',
  'TRADING/FOREIGN_EXCHANGE': 'מט"ח',
};

/** Labels for subs that arrive with `main: null` (live: 56 rows). */
export const NULL_MAIN_SUB_LABELS: Readonly<Record<string, string>> = {
  'GOVERNMENT SERVICES': 'שירותי ממשלה',
  'SHOPPING_OTHER': 'קניות — אחר',
  'BUSINESS_EXPENSES': 'הוצאות עסקיות',
  'HEALTHCARE': 'רפואה',
  'ELECTRONICS': 'אלקטרוניקה',
  'BOOKS_&_GAMES': 'ספרים ומשחקים',
};

/** Resolved label set for one category pair. */
export interface CategoryLabel {
  main: string;
  sub: string | null;
  icon: string;
  /** False when either key fell back to "אחר". */
  known: boolean;
}

/**
 * True when both `main` and `sub` have an explicit Hebrew label here (strict — used by the snapshot
 * test to fail on any unmapped live pair). `main: null` is accepted for subs in {@link NULL_MAIN_SUB_LABELS}.
 */
export function hasCategoryLabel(main: string | null | undefined, sub: string | null | undefined): boolean {
  if (main === null || main === undefined) {
    return sub !== null && sub !== undefined && sub in NULL_MAIN_SUB_LABELS;
  }
  if (!(main in MAIN_LABELS)) return false;
  if (sub === null || sub === undefined) return true;
  return `${main}/${sub}` in SUB_LABELS;
}

/**
 * Hebrew labels + icon for a category pair, falling back to "אחר" for unknown keys.
 */
export function categoryLabel(main: string | null | undefined, sub: string | null | undefined): CategoryLabel {
  const known = hasCategoryLabel(main, sub);
  if (main === null || main === undefined) {
    const subLabel = sub ? NULL_MAIN_SUB_LABELS[sub] ?? null : null;
    return { main: subLabel ?? GROUPS.OTHER.label, sub: subLabel, icon: GROUPS.OTHER.icon, known };
  }
  const m = MAIN_LABELS[main];
  const subLabel = sub ? SUB_LABELS[`${main}/${sub}`] ?? null : null;
  return {
    main: m?.label ?? GROUPS.OTHER.label,
    sub: subLabel,
    icon: m?.icon ?? GROUPS.OTHER.icon,
    known,
  };
}

const DINING_FOOD_SUBS = new Set(['RESTAURANT', 'COFFEE_&_SNACKS', 'BARS', 'ALCOHOL_&_TOBACCO']);
const VEHICLE_RE = /רכב/;

/** Minimal input for {@link groupKeyFor}. */
export interface GroupInput {
  categoryMain: string | null;
  categorySub: string | null;
  merchantKey: string;
}

/**
 * Binding mapping of observed Financy keys to the 10 personal display groups (ARCHITECTURE §4 table).
 * Everything not listed — `main: null`, `UNCATEGORIZED`, `FINANCE/*`, `INCOMES_EXPENSES/OTHER`,
 * unmatched `TRANSFER/*`, other `LOAN_TRANSACTION/LOAN`, `RETURN/*` … — is `OTHER`.
 */
export function groupKeyFor(tx: GroupInput): GroupKey {
  const main = tx.categoryMain;
  const sub = tx.categorySub ?? '';
  switch (main) {
    case 'HOUSEHOLD_&_SERVICES':
      if (sub === 'UTILITIES') return 'HOUSING';
      if (sub === 'INSURANCE_&_FEES') return 'INSURANCE';
      if (sub === 'COMMUNICATIONS') return 'TELECOM';
      return 'OTHER';
    case 'HOME_IMPROVEMENTS':
      return 'HOUSING';
    case 'LOAN_TRANSACTION':
      if (sub === 'MORTGAGE') return 'HOUSING';
      if (sub === 'LOAN' && VEHICLE_RE.test(tx.merchantKey)) return 'TRANSPORT';
      return 'OTHER';
    case 'FOOD_&_DRINKS':
      if (sub === 'GROCERIES') return 'GROCERIES';
      if (DINING_FOOD_SUBS.has(sub)) return 'DINING';
      return 'OTHER';
    case 'OTHER':
      return sub === 'EDUCATION' ? 'KIDS' : 'OTHER';
    case 'TRANSPORT':
      return 'TRANSPORT';
    case 'LEISURE':
      return 'DINING';
    case 'SHOPPING':
      return 'SHOPPING';
    case 'INSURANCE':
      return 'INSURANCE';
    case 'HEALTH_&_BEAUTY':
      return 'HEALTH';
    default:
      return 'OTHER';
  }
}

/** How a row participates in the metrics (ARCHITECTURE §4 `spendingScope`). */
export type SpendingScope = 'spend' | 'income' | 'transfer' | 'card-charge' | 'savings' | 'tax' | 'fee-return' | 'ignore';

/** Per-row context the library cannot know on its own (local columns / account state). */
export interface ScopeContext {
  /** False when the row belongs to a non-canonical duplicate account. Default true. */
  canonical?: boolean;
  /** PENDING row that was superseded by a BOOKED row (local column). Default false. */
  superseded?: boolean;
  /** User marked the row as an internal transfer (local column). Default false. */
  internal?: boolean;
  /** The row was paired by {@link import('./normalize.ts').isOwnTransfer}. Default false. */
  ownTransfer?: boolean;
}

/** spec-personal §2.3 rule (ג): deposits to savings by description, for banks that hide the deposit account. */
export const SAVINGS_DESCRIPTION_RE = /(הפקדה לפיקדון|העברה לחיסכון|פיקדון)/;

const TAX_MAINS = new Set(['VAT', 'NATIONAL_INSURANCE', 'PENSION']);

/**
 * Classify a row for the metrics, evaluated in this order (ARCHITECTURE §4):
 * `ignore` (superseded, non-canonical, or `amountAgorot` null) → `card-charge` (any sign) →
 * `fee-return` (principal and fee rows) → `transfer` (own-transfer pair or user-marked internal) →
 * `savings` (`ASSETS/SAVINGS`, or rule (ג) on CHECKING) → `tax` (`VAT/*`, `NATIONAL_INSURANCE/*`,
 * `PENSION/*` debits) → `spend` for `RETURN/*` (credits reduce their category, never income) →
 * `income` (positive CHECKING rows) → `spend` (everything else, signed). Loan repayments are `spend`.
 */
export function spendingScope(tx: NormalizedTransaction, ctx: ScopeContext = {}): SpendingScope {
  if (ctx.superseded === true || ctx.canonical === false || tx.amountAgorot === null) return 'ignore';
  if (isCardChargeRow(tx)) return 'card-charge';
  if (isReturnedPayment(tx)) return 'fee-return';
  if (ctx.ownTransfer === true || ctx.internal === true) return 'transfer';
  const main = tx.categoryMain;
  if ((main === 'ASSETS' && tx.categorySub === 'SAVINGS') || (tx.kind === 'CHECKING' && SAVINGS_DESCRIPTION_RE.test(tx.description))) {
    return 'savings';
  }
  if (main !== null && TAX_MAINS.has(main) && tx.amountAgorot < 0) return 'tax';
  if (main === 'RETURN') return 'spend';
  if (tx.kind === 'CHECKING' && tx.amountAgorot > 0) return 'income';
  return 'spend';
}
